import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Clock } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './chartTheme';

function MiniLine({ data, yKey, yLabel, color, decimals }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yLabel} vs Time`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${yKey}`} />
      <ResponsiveContainer width="100%" height={240}>
        <LineChart
          data={data} margin={{ top: 4, right: 12, left: 4, bottom: 20 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="elapsed_min" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Elapsed Time (min)', position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }}
            tickFormatter={(v) => Number(v).toFixed(0)}
          />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={(v) => `t: ${Number(v).toFixed(1)} min`}
            formatter={(v) => Number(v).toFixed(decimals)}
          />
          <Line type="monotone" dataKey={yKey} stroke={color} {...LINE_PROPS} />
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * SoC vs Time and Energy Delivered vs Time (spec §6.2 charts 1 and 5): the actual charging
 * session timeline, time-zeroed at the user's Start SoC. SoC-vs-Time visibly flattens past
 * ~80% as the CV taper stretches out the remaining SoC gain per unit time.
 */
const ChargingSessionChart = ({ result }) => {
  const data = useMemo(() => {
    if (!result) return [];
    return result.session_elapsed_min.map((t, i) => ({
      elapsed_min: t,
      soc_pct: result.session_soc_pct[i],
      cumulative_energy_kwh: result.session_cumulative_energy_kwh[i],
    }));
  }, [result]);

  if (data.length === 0) return null;

  return (
    <ChartCard
      icon={Clock}
      title="Charging Session vs Time"
      description="SoC and cumulative energy delivered over the actual charging session."
      testId="chart-charging-session"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <MiniLine data={data} yKey="soc_pct" yLabel="SoC (%)" color={SERIES_COLORS[0]} decimals={1} />
        <MiniLine data={data} yKey="cumulative_energy_kwh" yLabel="Energy Delivered (kWh)" color={SERIES_COLORS[4]} decimals={2} />
      </div>
    </ChartCard>
  );
};

export default ChargingSessionChart;
